import ms from 'ms';
import { useCallback } from 'react';

import { ShippingMethods } from '@/enums/shippingMethod';
import {
    paymentStatusPrimaryMessages,
    paymentStatusSecondaryMessages,
} from '@/pages/WonItems/components/WonItemsCard/components/StatusBadge/helpers/badgeTextMessages';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogAddress } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { openShippingAndPaymentModal } from '@/redux/modules/modal';
import { createFullAddressString } from '@/utils/address/createAddressString';

export type GetWonCatalogStatusParams = {
    addShippingDate?: Date;
    catalogId: number;
    invoiceId?: number;
    lastPaymentDate?: Date;
    primaryStatus: string;
    secondaryStatus?: string;
    shippingAmount?: number;
    shippingAvailable?: boolean;
    shippingCurrency?: string;
    shippingMethod?: ShippingMethods;
    trackingLink?: string;
};

export const useGetWonCatalogStatus = ({
    addShippingDate,
    catalogId,
    invoiceId,
    lastPaymentDate,
    primaryStatus,
    secondaryStatus,
    shippingAmount,
    shippingAvailable,
    shippingCurrency,
    shippingMethod,
}: GetWonCatalogStatusParams): {
    primaryText: string | JSX.Element;
    secondaryText: string | JSX.Element;
} => {
    const dispatch = useAppDispatch();
    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));

    const isPaid = primaryStatus === 'paid' || primaryStatus === 'Invoice Paid';

    const openShipAndPayModal = useCallback(() => dispatch(openShippingAndPaymentModal()), [dispatch]);
    let primaryText: string | JSX.Element;
    let secondaryText: string | JSX.Element;
    switch (shippingMethod) {
        case ShippingMethods.LiveShipping:
            if (primaryStatus === 'Order Created') {
                primaryText = paymentStatusPrimaryMessages[primaryStatus];
                secondaryText = paymentStatusSecondaryMessages.paymentPending;
            } else {
                primaryText = primaryStatus;
                secondaryText = secondaryStatus;
            }
            break;
        case ShippingMethods.BidderShipping:
            primaryText = paymentStatusPrimaryMessages[primaryStatus];
            secondaryText = paymentStatusSecondaryMessages[ShippingMethods.BidderShipping];
            break;
        case ShippingMethods.Pickup:
            primaryText = paymentStatusPrimaryMessages[primaryStatus];
            secondaryText = paymentStatusSecondaryMessages[ShippingMethods.Pickup](
                createFullAddressString(
                    address.address1,
                    address.city,
                    address.state,
                    address.postalCode,
                    address.country,
                    address.address2
                )
            );
            break;
        default:
            break;
    }

    if (invoiceId === 0) {
        primaryText = paymentStatusPrimaryMessages.invoicePending;
    }

    if (isPaid && shippingAvailable && Boolean(addShippingDate)) {
        const withinAddShippingDeadline = addShippingDate.getTime() > Date.now();

        if (withinAddShippingDeadline) {
            secondaryText = paymentStatusSecondaryMessages['addShippingEligible'](
                shippingAmount,
                shippingCurrency,
                addShippingDate
            );
        }

        if (!withinAddShippingDeadline) {
            secondaryText = paymentStatusSecondaryMessages['addShippingIneligible'](openShipAndPayModal);
        }
    }

    if (
        (primaryStatus === 'Invoice Pending' || primaryStatus === 'Order Created') &&
        !isPaid &&
        ShippingMethods.LiveShipping
    ) {
        primaryText = paymentStatusPrimaryMessages['OrderCreated'];
        secondaryText = paymentStatusSecondaryMessages['youWillSeeStatus'];
    }

    if (!primaryText) {
        const wasPaidTwoWeeksAgo =
            Boolean(lastPaymentDate) && lastPaymentDate.getTime() > 0
                ? Date.now() - lastPaymentDate.getTime() > ms('2 weeks')
                : false;
        const messageKey = wasPaidTwoWeeksAgo ? 'orderComplete' : primaryStatus;
        primaryText = paymentStatusPrimaryMessages[messageKey] || primaryStatus;
    }

    if (!secondaryText && !shippingAvailable) {
        const wasPaidTwoWeeksAgo =
            Boolean(lastPaymentDate) && lastPaymentDate.getTime() > 0
                ? Date.now() - lastPaymentDate.getTime() > ms('2 weeks')
                : false;

        let messageKey = primaryStatus;
        if (wasPaidTwoWeeksAgo) {
            messageKey = 'orderComplete';
        } else if (isPaid && !shippingMethod) {
            messageKey = ShippingMethods.BidderShipping;
        }

        secondaryText = paymentStatusSecondaryMessages[secondaryStatus || messageKey] || secondaryStatus;
    }

    return { primaryText, secondaryText };
};
